import {
  useGetCandidateProcesses,
  usePostCandidateMessage,
  type CandidateProcessRequestParams,
} from '@entities/candidates';
import { useOutgoingMessages } from '@packages/chat/react';
import { showNotification } from '@shared/utils';
import { useMemo } from 'react';
import {
  toLocalChatItem,
  toServerChatItem,
} from '../utils/candidateInterviewChatItems';
import { getSendErrorMessage } from '../utils/getSendErrorMessage';

const POLL_INTERVAL = 10_000;

export const useCandidateInterviewChat = (
  candidateId: string,
  params: CandidateProcessRequestParams,
  isVisible: boolean,
  candidateName: string
) => {
  // A chat outside the viewport is not polled.
  const history = useGetCandidateProcesses(
    candidateId,
    params,
    isVisible ? POLL_INTERVAL : false
  );
  const mutation = usePostCandidateMessage(candidateId);
  const outgoing = useOutgoingMessages({
    sessionId: history.sessionId,
    canSend: Boolean(candidateId),
    successfulRefresh: history.successfulRefresh,
    getLatestStartedRequestId: history.getLatestStartedRequestId,
    send: mutation.mutateAsync,
    onSendFailed: (_clientId, error) =>
      showNotification({
        error,
        variant: 'error',
        message: getSendErrorMessage(error),
      }),
  });

  const items = useMemo(
    () => [
      ...outgoing.localMessages.map(toLocalChatItem),
      ...history.messages.map(process =>
        toServerChatItem(process, candidateName)
      ),
    ],
    [outgoing.localMessages, history.messages, candidateName]
  );

  return {
    items,
    history,
    sendMessage: outgoing.sendMessage,
    isSending: mutation.isPending,
    // Local rows are the newest: the user's own send moves the view.
    scrollToLatestKey: outgoing.localMessages.at(0)?.clientId,
  };
};

export type CandidateInterviewChat = ReturnType<
  typeof useCandidateInterviewChat
>;
